import React, { useCallback, useReducer } from 'react';
import {
	CreateTeaserContext,
	createTeaserInitialState,
	ICreateTeaserTotalSchedule,
	TCreateTeaserControlPoint,
	TCreateTeaserSelectedContentType,
	TCreateTeaserSelectedItem,
	TCreateTeaserTransitionType,
	TCreateTeaserVisibleForm,
	TSourceUrlLocally,
	TTypeOfCreateTeaserModalWindow,
} from './createTeaserContext';
import { createTeaserReducer } from './createTeaserReducer';
import { CreateTeaserActionTypes } from './createTeaserActions';
import { IItemForGetShortInfo, TFileId, ICreateTeaser } from 'types';

const CreateTeaserState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createTeaserReducer, createTeaserInitialState);

	const changeVisibleForm = useCallback((value: TCreateTeaserVisibleForm) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setCustomer = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setContentType = useCallback((value: TCreateTeaserSelectedContentType) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_CONTENT_TYPE,
			payload: {
				contentType: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_SCHEDULE,
			payload: {
				schedule: value,
			},
		});
	}, []);

	const changeAdditionalSchedule = useCallback(
		(fieldId: string, value: string) => {
			const updatedSchedules = state.totalSchedules.map((el) => (el.id === fieldId ? { ...el, value } : el));

			dispatch({
				type: CreateTeaserActionTypes.CHANGE_ADDITIONAL_SCHEDULE,
				payload: {
					updatedSchedules,
				},
			});
		},
		[state.totalSchedules],
	);

	const setSchedules = useCallback((value: ICreateTeaserTotalSchedule[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_SCHEDULES,
			payload: {
				updatedSchedules: value,
			},
		});
	}, []);

	const setTransitionType = useCallback(
		(value: TCreateTeaserTransitionType) => {
			dispatch({
				type: CreateTeaserActionTypes.SET_TRANSITION_TYPE,
				payload: {
					transitionType: state.selectedTransitionType === value ? null : value,
				},
			});
		},
		[state.selectedTransitionType],
	);

	const setFetchedDishCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_DISH_CATEGORIES,
			payload: {
				dishCategories: value,
			},
		});
	}, []);

	const setTransitionDishCategory = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_TRANSITION_DISH_CATEGORY,
			payload: {
				transitionDishCategory: value,
			},
		});
	}, []);

	const setFetchedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_DISH_SUB_CATEGORIES,
			payload: {
				dishSubCategories: value,
			},
		});
	}, []);

	const setTransitionDishSubCategory = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_TRANSITION_DISH_SUB_CATEGORY,
			payload: {
				transitionDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedDishes = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setTransitionDishName = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_TRANSITION_DISH_NAME,
			payload: {
				transitionDishName: value,
			},
		});
	}, []);

	const setFetchedGameCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_GAME_CATEGORIES,
			payload: {
				gameCategories: value,
			},
		});
	}, []);

	const setTransitionGameCategory = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_TRANSITION_GAME_CATEGORY,
			payload: {
				transitionGameCategory: value,
			},
		});
	}, []);

	const setFetchedGames = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_FETCHED_GAMES,
			payload: {
				games: value,
			},
		});
	}, []);

	const setTransitionGameName = useCallback((value: TCreateTeaserSelectedItem) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_TRANSITION_GAME_NAME,
			payload: {
				transitionGameName: value,
			},
		});
	}, []);

	const setControlPoint = useCallback((value: TCreateTeaserControlPoint) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_CONTROL_POINT,
			payload: {
				controlPoint: value,
			},
		});
	}, []);

	const changeDelayTime = useCallback((value: string) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_DELAY_TIME,
			payload: {
				delayTime: value,
			},
		});
	}, []);

	const changeLaunchCount = useCallback((value: string) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_LAUNCH_COUNT,
			payload: {
				launchCount: value,
			},
		});
	}, []);

	const changeCountDown = useCallback((value: string) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_COUNTDOWN,
			payload: {
				countDown: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: ICreateTeaser | null) => {
		dispatch({
			type: CreateTeaserActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateTeaserModalWindow) => {
		dispatch({
			type: CreateTeaserActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => {
		dispatch({
			type: CreateTeaserActionTypes.RESET_FIELDS,
		});
	}, []);

	return (
		<CreateTeaserContext.Provider
			value={{
				changeVisibleForm,
				setFile,
				setFetchedCustomers,
				setCustomer,
				setFetchedRestaurants,
				setRestaurants,
				setContentType,
				changeDescription,
				changeSchedule,
				changeAdditionalSchedule,
				setSchedules,
				setTransitionType,
				setFetchedDishCategories,
				setTransitionDishCategory,
				setFetchedDishSubCategories,
				setTransitionDishSubCategory,
				setFetchedDishes,
				setTransitionDishName,
				setFetchedGameCategories,
				setTransitionGameCategory,
				setFetchedGames,
				setTransitionGameName,
				setControlPoint,
				changeDelayTime,
				changeLaunchCount,
				changeCountDown,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				fetchedCustomers: state.fetchedCustomers,
				selectedCustomer: state.selectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				selectedRestaurants: state.selectedRestaurants,
				selectedContentType: state.selectedContentType,
				description: state.description,
				schedule: state.schedule,
				totalSchedules: state.totalSchedules,
				selectedTransitionType: state.selectedTransitionType,
				fetchedDishCategories: state.fetchedDishCategories,
				selectedTransitionDishCategory: state.selectedTransitionDishCategory,
				fetchedDishSubCategories: state.fetchedDishSubCategories,
				selectedTransitionDishSubCategory: state.selectedTransitionDishSubCategory,
				fetchedDishes: state.fetchedDishes,
				selectedTransitionDishName: state.selectedTransitionDishName,
				fetchedGameCategories: state.fetchedGameCategories,
				selectedTransitionGameCategory: state.selectedTransitionGameCategory,
				fetchedGames: state.fetchedGames,
				selectedTransitionGameName: state.selectedTransitionGameName,
				selectedControlPoint: state.selectedControlPoint,
				delayTime: state.delayTime,
				launchCount: state.launchCount,
				countDown: state.countDown,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateTeaserContext.Provider>
	);
};

export default CreateTeaserState;
